import type { MouseEvent, Ref } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { PixelPlaceholderLoader } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { InfoTooltip } from "app/pages/digital-marketing/common/ui/InfoTooltip";

import { IconButton, MetricCardTitle, MetricCardWrapper } from "./metric-card.styled";

interface IMetricCardProps {
    title: string;
    value?: string | number;
    tooltip?: string;
    valueDescription?: string;
    action?: (event: MouseEvent<HTMLDivElement>) => void;
    isLoading?: boolean;
    isDisabled?: boolean;
    ref?: Ref<HTMLDivElement>;
}

const MetricCard = (props: IMetricCardProps) => {
    const { title, value = 0, tooltip, valueDescription, action, isLoading, isDisabled, ref } = props;

    const handleActionClick = (event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.preventDefault();

        if (isDisabled || isLoading) {
            return;
        }

        action?.(event);
    };

    return (
        <MetricCardWrapper
            gap="16px"
            onClick={handleActionClick}
            isLoading={isLoading}
            isDisabled={isDisabled}
            data-automation="metric-card"
            $isActionable={!!action}
            ref={ref}
        >
            <Flex
                justifyContent="space-between"
                alignItems="center"
            >
                <Flex
                    gap="8px"
                    alignItems="center"
                >
                    <MetricCardTitle>{title}</MetricCardTitle>
                    {tooltip ? <InfoTooltip content={tooltip} /> : null}
                </Flex>
                <IconButton hidden={isDisabled || !action}>
                    <SWReactIcons
                        iconName="arrow-slant-right"
                        size="16px"
                    />
                </IconButton>
            </Flex>
            {isLoading ? (
                <Flex
                    width="100%"
                    alignItems="flex-end"
                    gap="8px"
                >
                    <PixelPlaceholderLoader
                        height={28}
                        width={100}
                    />
                    <PixelPlaceholderLoader
                        height={16}
                        width={80}
                    />
                </Flex>
            ) : (
                <Flex
                    gap="8px"
                    alignItems="flex-end"
                >
                    <Text
                        fontVariant="h4"
                        color={isDisabled ? colorsPalettes.carbon["300"] : colorsPalettes.carbon["500"]}
                    >
                        {value}
                    </Text>
                    {valueDescription ? (
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.carbon["300"]}
                            margin="0 0 3px 0"
                        >
                            {valueDescription}
                        </Text>
                    ) : null}
                </Flex>
            )}
        </MetricCardWrapper>
    );
};

export default MetricCard;
